import axios, { AxiosInstance, AxiosResponse } from 'axios';
import { AuthResponse, SessionStatus, User, ApiResponse, PaginatedApiResponse, TaskProposal } from '../types';

class ApiService {
  private api: AxiosInstance;
  private baseURL: string;
  private csrfTokenPromise: Promise<string | null> | null = null;
  private pendingRequests: Map<string, Promise<any>> = new Map();
  private retryConfig = {
    maxRetries: 0,
    retryDelay: 0,
    retryStatusCodes: [],
  };

  constructor() {
    this.baseURL = process.env.REACT_APP_API_URL || process.env.REACT_APP_API_BASE_URL || 'https://demo.api.workwallet.thehps.in';
    this.api = axios.create({
      baseURL: this.baseURL,
      timeout: 10000,
      withCredentials: true,
      headers: {
        Accept: 'application/json'
      }
    });

    this.api.interceptors.request.use(
      async (config) => {
        if (['post', 'put', 'delete', 'patch'].includes(config.method?.toLowerCase() || '')) {
          let csrfToken = this.getCSRFToken();
          if (!csrfToken) {
            try {
              csrfToken = await this.getCSRFTokenFromServerCached();
            } catch (error) {
              console.warn('Failed to get CSRF token from server:', error);
            }
          }

          if (csrfToken) {
            config.headers['X-CSRFToken'] = csrfToken;
          }
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    this.api.interceptors.response.use(
      (response) => response,
      (error) => {
        const status = error.response?.status;
        const url: string = error.config?.url || '';

        const isAuthFlow = (
          url.includes('/api/auth/login/') ||
          url.includes('/api/auth/logout/') ||
          url.includes('/api/auth/csrf-token/') ||
          url.includes('/api/auth/session-status/')
        );

        if (!isAuthFlow && (status === 401 || status === 403)) {
          this.handleAuthError(error.response);
        }
        return Promise.reject(error);
      }
    );
  }

  private getCSRFToken(): string | null {
    const metaToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');
    if (metaToken) return metaToken;

    const formToken = document.querySelector('[name=csrfmiddlewaretoken]')?.getAttribute('value');
    if (formToken) return formToken;

    return this.getCookie('csrftoken');
  }

  async getCSRFTokenFromServerCached(): Promise<string | null> {
    if (this.csrfTokenPromise) {
      return this.csrfTokenPromise;
    }

    this.csrfTokenPromise = this.getCSRFTokenFromServer();

    try {
      const result = await this.csrfTokenPromise;
      return result;
    } finally {
      this.csrfTokenPromise = null;
    }
  }

  async getCSRFTokenFromServer(): Promise<string | null> {
    try {
      const response = await this.api.get('/api/auth/csrf-token/');
      return response.data.csrf_token;
    } catch (error) {
      console.warn('Failed to get CSRF token from server:', error);
      return null;
    }
  }

  private getCookie(name: string): string | null {
    let cookieValue = null;
    if (document.cookie && document.cookie !== '') {
      const cookies = document.cookie.split(';');
      for (let i = 0; i < cookies.length; i++) {
        const cookie = cookies[i].trim();
        if (cookie.substring(0, name.length + 1) === (name + '=')) {
          cookieValue = decodeURIComponent(cookie.substring(name.length + 1));
          break;
        }
      }
    }
    return cookieValue;
  }

  private handleAuthError(response: AxiosResponse) {
    const data = response.data;
    if (data?.status === 'session_expired' || data?.session_status === 'expired' || response.status === 401) {
      window.dispatchEvent(new CustomEvent('session-expired', {
        detail: {
          message: data?.message || 'Session expired. Please login again.',
          redirectUrl: data?.redirect_url || '/auth/signin'
        }
      }));
    } else if (data?.redirect_url) {
      window.location.href = data.redirect_url;
    }
  }

  private generateRequestKey(method: string, url: string, data?: any): string {
    const dataString = data ? JSON.stringify(data) : '';
    return `${method.toUpperCase()}:${url}:${dataString}`;
  }

  private async executeRequest<T>(
    requestFn: () => Promise<T>
  ): Promise<T> {
    return await requestFn();
  }

  private async makeRequest<T>(
    method: 'get' | 'post' | 'put' | 'patch' | 'delete',
    url: string,
    data?: any,
    config?: any
  ): Promise<T> {
    const requestKey = this.generateRequestKey(method, url, data);

    if (this.pendingRequests.has(requestKey)) {
      console.log(`Deduplicating request: ${requestKey}`);
      return this.pendingRequests.get(requestKey)!;
    }

    const requestPromise = this.executeRequest(async () => {
      let response: AxiosResponse;

      switch (method) {
        case 'get':
          response = await this.api.get(url, config);
          break;
        case 'post':
          response = await this.api.post(url, data, config);
          break;
        case 'put':
          response = await this.api.put(url, data, config);
          break;
        case 'patch':
          response = await this.api.patch(url, data, config);
          break;
        case 'delete':
          response = await this.api.delete(url, config);
          break;
        default:
          throw new Error(`Unsupported HTTP method: ${method}`);
      }

      return response.data;
    });

    this.pendingRequests.set(requestKey, requestPromise);

    try {
      const result = await requestPromise;
      return result;
    } finally {
      this.pendingRequests.delete(requestKey);
    }
  }

  private async initializeCSRFToken(): Promise<void> {
    try {
      const response = await this.api.get('/api/auth/csrf-token/');
    } catch (error) {
      console.log('CSRF token initialization completed');
    }
  }

  async initializeCSRF(): Promise<void> {
    await this.initializeCSRFToken();
  }

  async login(credentials: { username: string; password: string; remember_me?: boolean }): Promise<AuthResponse> {
    return this.makeRequest('post', '/api/auth/login/', credentials);
  }

  async logout(): Promise<void> {
    this.clearPendingRequests();
    console.log('API: Calling logout endpoint...');
    try {
      const response = await this.api.post('/api/auth/logout/');
      console.log('API: Logout response:', response.data);
    } catch (error: any) {
      console.warn('API: Logout error:', error.response?.data || error.message);
    }
  }

  async getSessionStatus(): Promise<SessionStatus> {
    return this.makeRequest('get', '/api/auth/session-status/');
  }

  async getCurrentUser(): Promise<User> {
    return this.makeRequest('get', '/api/auth/user/');
  }

  async getGroupsList(params?: {
    page?: number;
    per_page?: number;
    search?: string;
    status?: string
  }): Promise<PaginatedApiResponse<any[]>> {
    const response = await this.api.get('/api/groups/', { params });
    return response.data;
  }

  async getGroupDetails(groupId: string): Promise<ApiResponse> {
    const response = await this.api.get('/api/groups/get/', {
      params: { group_id: groupId }
    });
    return response.data;
  }

  async addGroup(data: {
    name: string;
    description: string;
    is_active: boolean;
    team_lead_id: string;
  }): Promise<ApiResponse> {
    const response = await this.api.post('/api/groups/create/', data);
    return response.data;
  }

  async updateGroup(groupId: string, data: {
    name?: string;
    description?: string;
    is_active?: boolean;
    team_lead_id?: string;
  }): Promise<ApiResponse> {
    const updateData: any = { ...data };
    if (updateData.team_lead_id !== undefined) {
      updateData.team_lead = updateData.team_lead_id;
      delete updateData.team_lead_id;
    }

    const response = await this.api.post(`/api/groups/${groupId}/update/`, updateData);
    return response.data;
  }
  async getTaskCategories(groupId?: string, filter?: 'global' | 'group' | 'all'): Promise<any> {
    let url = groupId ? `/api/categories/${groupId}/` : '/api/categories/';

    if (groupId && filter && filter !== 'all') {
      url += `?filter=${filter}`;
    }

    const response = await this.api.get(url);
    return response.data;
  }

  async createTaskCategory(data: {
    name: string;
    description: string;
    color: string;
    icon: string;
    priority: string;
    is_global: boolean;
    base_points: number;
    team_lead_point_type: string;
    team_lead_percentage?: number;
    team_lead_fixed_points?: number;
    distribution_method: string;
    deadline_rules?: any[];
  }, groupId?: string): Promise<any> {
    const url = groupId ? `/api/categories/create/${groupId}/` : '/api/categories/create/';
    const response = await this.api.post(url, data);
    return response.data;
  }

  async updateTaskCategory(categoryId: string, data: {
    name: string;
    description: string;
    color: string;
    icon: string;
    priority: string;
    is_global: boolean;
    base_points: number;
    team_lead_point_type: string;
    team_lead_percentage?: number;
    team_lead_fixed_points?: number;
    distribution_method: string;
    deadline_rules?: any[];
  }): Promise<any> {
    const response = await this.api.put(`/api/categories/${categoryId}/update/`, data);
    return response.data;
  }

  async deleteTaskCategory(categoryId: string): Promise<any> {
    const response = await this.api.delete(`/api/categories/${categoryId}/delete/`);
    return response.data;
  }

  async getUsersList(params?: {
    page?: number;
    per_page?: number;
    search?: string;
    role?: string;
    status?: string
  }): Promise<PaginatedApiResponse<User[]>> {
    const response = await this.api.get('/api/users/list/', { params });
    return response.data;
  }

  async getUserDetails(userId: string): Promise<ApiResponse> {
    const response = await this.api.get('/api/users/get/', {
      params: { user_id: userId }
    });
    return response.data;
  }

  async addUser(userData: {
    full_name: string;
    email: string;
    phone?: string;
    status: string;
    designation?: string;
  }): Promise<ApiResponse> {
    const response = await this.api.post('/api/users/add/', userData);
    return response.data;
  }

  async updateUser(userData: {
    user_id: string;
    full_name?: string;
    email?: string;
    phone?: string;
    status?: string;
    designation?: string;
  }): Promise<ApiResponse> {
    const response = await this.api.post('/api/users/update/', userData);
    return response.data;
  }

  async deactivateUser(userId: string): Promise<ApiResponse> {
    const response = await this.api.post('/api/users/deactivate/', { user_id: userId });
    return response.data;
  }

  async activateUser(userId: string): Promise<ApiResponse> {
    const response = await this.api.post('/api/users/activate/', { user_id: userId });
    return response.data;
  }

  async deleteUser(userId: string): Promise<ApiResponse> {
    const response = await this.api.post('/api/users/delete/', { user_id: userId });
    return response.data;
  }

  async bulkDeactivateUsers(userIds: string[]): Promise<ApiResponse> {
    const response = await this.api.post('/api/users/bulk-deactivate/', { user_ids: userIds });
    return response.data;
  }

  async bulkActivateUsers(userIds: string[]): Promise<ApiResponse> {
    const response = await this.api.post('/api/users/bulk-activate/', { user_ids: userIds });
    return response.data;
  }

  async bulkDeleteUsers(userIds: string[]): Promise<ApiResponse> {
    const response = await this.api.post('/api/users/bulk-delete/', { user_ids: userIds });
    return response.data;
  }

  async requestPasswordReset(email: string): Promise<ApiResponse> {
    const response = await this.api.post('/api/users/request-password-reset/', {
      email: email
    });
    return response.data;
  }

  async resetUserPassword(userId: string, newPassword?: string): Promise<ApiResponse> {
    const response = await this.api.post('/api/users/reset-password/', {
      user_id: userId,
      new_password: newPassword
    });
    return response.data;
  }

  async bulkResetPasswords(userIds: string[]): Promise<ApiResponse> {
    const response = await this.api.post('/api/users/bulk-reset-passwords/', { user_ids: userIds });
    return response.data;
  }

  async exportUsersExcel(params?: {
    search?: string;
    role?: string;
    status?: string
  }): Promise<Blob> {
    const response = await this.api.get('/api/users/export/', {
      params,
      responseType: 'blob'
    });
    return response.data;
  }

  async importUsersExcel(file: File): Promise<ApiResponse> {
    const formData = new FormData();
    formData.append('file', file);
    const response = await this.api.post('/api/users/import/', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  async downloadUsersImportSample(): Promise<Blob> {
    const response = await this.api.get('/api/users/import/sample/', {
      responseType: 'blob'
    });
    return response.data;
  }


  async getGroups(params?: { search?: string; category?: string; status?: string }): Promise<ApiResponse> {
    const response = await this.api.get('/api/groups/', { params });
    return response.data;
  }

  async getGroupById(id: string): Promise<ApiResponse> {
    const response = await this.api.get(`/api/groups/${id}/`);
    return response.data;
  }

  async getUserGroups(params?: {
    page?: number;
    per_page?: number;
    search?: string;
    category?: string;
    status?: string
  }): Promise<ApiResponse> {
    // Use the specific user groups endpoint for more reliable membership data
    const response = await this.api.get('/api/user/groups/', { params });
    return response.data;
  }




  async getGroupOverviewData(groupId: string): Promise<ApiResponse<any>> {
    const response = await this.api.get(`/api/groups/${groupId}/overview/`);
    return response.data;
  }

  async getGroupLeaderboard(groupId: string, params?: {
    period?: string;
    limit?: number;
    page?: number;
    sort_by?: string;
    category?: string;
    tab?: string;
    search?: string;
  }): Promise<PaginatedApiResponse> {
    const queryParams: any = { ...params };
    // Remove category as it's no longer used
    delete queryParams.category;
    const response = await this.api.get(`/api/groups/${groupId}/leaderboard/`, { params: queryParams });
    return response.data;
  }

  async getLeaderboardSummary(groupId: string, params?: {
    period?: string;
  }): Promise<ApiResponse> {
    const response = await this.api.get(`/api/groups/${groupId}/leaderboard/summary/`, { params });
    return response.data;
  }

  async getUserLeaderboardStats(groupId: string, userId: string, params?: {
    period?: string;
  }): Promise<ApiResponse> {
    const response = await this.api.get(`/api/groups/${groupId}/leaderboard/user/${userId}/`, { params });
    return response.data;
  }

  // Global Leaderboard API methods
  async getGlobalLeaderboard(params?: {
    period?: string;
    limit?: number;
    page?: number;
    sort_by?: string;
    category?: string;
    tab?: string;
    search?: string;
    group_ids?: string[];
  }): Promise<PaginatedApiResponse> {
    const queryParams: any = { ...params };
    if (params?.group_ids && params.group_ids.length > 0) {
      queryParams.group_ids = params.group_ids.join(',');
    }
    // Remove category as it's no longer used
    delete queryParams.category;
    const response = await this.api.get('/api/leaderboard/global/', { params: queryParams });
    return response.data;
  }

  async getGlobalLeaderboardSummary(params?: {
    period?: string;
    group_ids?: string[];
  }): Promise<ApiResponse> {
    const queryParams: any = { ...params };
    if (params?.group_ids && params.group_ids.length > 0) {
      queryParams.group_ids = params.group_ids.join(',');
    }
    const response = await this.api.get('/api/leaderboard/global/summary/', { params: queryParams });
    return response.data;
  }

  // Badge Management API methods
  async getBadges(params?: {
    scope?: 'global' | 'group' | 'all';
    group_id?: string;
    badge_type?: string;
    is_active?: boolean;
  }): Promise<ApiResponse> {
    const response = await this.api.get('/api/badges/', { params });
    return response.data;
  }

  async createBadge(badgeData: {
    name: string;
    description: string;
    badge_type: string;
    scope: 'global' | 'group';
    criteria_config: any;
    group_id?: string;
    image?: string;
    image_alt_text?: string;
    sort_order?: number;
    is_active?: boolean;
    is_visible?: boolean;
  }): Promise<ApiResponse> {
    const response = await this.api.post('/api/badges/create/', badgeData);
    return response.data;
  }

  async updateBadge(badgeId: string, badgeData: {
    name?: string;
    designation?: string;
    display_designation?: string;
    profile_photo?: string | null;
    criteria_config?: any;
    image?: string;
    image_alt_text?: string;
    sort_order?: number;
    is_active?: boolean;
    is_visible?: boolean;
  }): Promise<ApiResponse> {
    const response = await this.api.put(`/api/badges/${badgeId}/update/`, badgeData);
    return response.data;
  }

  async deleteBadge(badgeId: string): Promise<ApiResponse> {
    const response = await this.api.delete(`/api/badges/${badgeId}/delete/`);
    return response.data;
  }

  async getCurrentUserBadges(params?: {
    group_id?: string;
    scope?: 'global' | 'group' | 'all';
  }): Promise<ApiResponse> {
    const response = await this.api.get('/api/badges/my-badges/', { params });
    return response.data;
  }

  async getUserBadges(userId: string, params?: {
    group_id?: string;
    scope?: 'global' | 'group' | 'all';
  }): Promise<ApiResponse> {
    const response = await this.api.get(`/api/badges/user/${userId}/`, { params });
    return response.data;
  }

  async getBadgeConfiguration(params?: {
    group_id?: string;
    scope?: 'global' | 'group';
  }): Promise<ApiResponse> {
    const response = await this.api.get('/api/badges/configuration/', { params });
    return response.data;
  }

  async updateBadgeConfiguration(configData: {
    scope: 'global' | 'group';
    group_id?: string;
    auto_assign_enabled?: boolean;
    check_frequency_hours?: number;
    notify_on_earn?: boolean;
    notify_admins?: boolean;
    show_in_profile?: boolean;
    show_in_leaderboard?: boolean;
    config_data?: any;
  }): Promise<ApiResponse> {
    const response = await this.api.post('/api/badges/configuration/update/', configData);
    return response.data;
  }

  // Group Settings API methods
  async getGroupSettings(groupId: string): Promise<ApiResponse> {
    const response = await this.api.get(`/api/groups/${groupId}/settings/`);
    return response.data;
  }

  async updateGroupSettings(groupId: string, data: any): Promise<ApiResponse> {
    // If files are present, use FormData; otherwise use JSON
    const hasFiles = data.logo || data.banner;

    if (hasFiles) {
      const formData = new FormData();

      // Add text fields
      if (data.name) formData.append('name', data.name);
      if (data.description) formData.append('description', data.description);
      if (data.theme_color) formData.append('theme_color', data.theme_color);
      if (data.email_notifications !== undefined) formData.append('email_notifications', data.email_notifications.toString());
      if (data.task_reminders !== undefined) formData.append('task_reminders', data.task_reminders.toString());
      if (data.weekly_reports !== undefined) formData.append('weekly_reports', data.weekly_reports.toString());

      // Add files
      if (data.logo) formData.append('logo', data.logo);
      if (data.banner) formData.append('banner', data.banner);

      const response = await this.api.post(`/api/groups/${groupId}/settings/save/`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } else {
      const response = await this.api.post(`/api/groups/${groupId}/settings/save/`, data);
      return response.data;
    }
  }

  async resetGroupSettings(groupId: string): Promise<ApiResponse> {
    const response = await this.api.post(`/api/groups/${groupId}/settings/reset/`);
    return response.data;
  }

  async deleteGroup(groupId: string, data?: { confirmation_name?: string }): Promise<ApiResponse> {
    const response = await this.api.post(`/api/groups/${groupId}/delete-confirm/`, data);
    return response.data;
  }

  async archiveGroup(groupId: string, data?: { reason?: string }): Promise<ApiResponse> {
    const response = await this.api.post(`/api/groups/${groupId}/archive/`, data);
    return response.data;
  }

  async getGroupPointConfiguration(groupId: string): Promise<ApiResponse> {
    const response = await this.api.get(`/api/groups/${groupId}/point-configuration/`);
    return response.data;
  }

  async updateGroupPointConfiguration(groupId: string, data: {
    task_completion_points?: number;
    participation_points?: number;
    bonus_points?: number;
    penalty_points?: number;
  }): Promise<ApiResponse> {
    const response = await this.api.put(`/api/groups/${groupId}/point-configuration/`, data);
    return response.data;
  }

  // Task management
  async getGroupTasks(groupId: string, params?: {
    search?: string;
    status?: string;
    priority?: string;
    category?: string;
    assignee?: string;
    page?: number;
    per_page?: number;
    work_mode?: boolean;
    exclude_templates?: boolean;
    templates_only?: boolean;
    instances_only?: boolean;
  }): Promise<ApiResponse> {
    const response = await this.api.get(`/api/groups/${groupId}/tasks/`, { params });
    return response.data;
  }

  // Get available task templates for current user
  async getAvailableTemplates(): Promise<ApiResponse> {
    const response = await this.api.get('/api/tasks/templates/available/');
    return response.data;
  }

  async createTask(taskData: {
    group_id: string;
    name: string;
    description: string;
    category_id: string;
    priority: string;
    assignee_ids: string[];
    due_date?: string;
    estimated_hours?: number;
    approval_type?: 'none' | 'single' | 'multiple';
    approver_ids?: string[];
    requires_approval?: boolean;
    requires_documents?: boolean;
    enable_group_chat?: boolean;
    distribution_method?: string;
    direct_assignment_type?: 'instances' | 'single';
    point_allocation?: Record<string, number>;
    work_descriptions?: Record<string, string>;
    is_recurring?: boolean;
    recurrence_type?: 'none' | 'daily' | 'weekly' | 'monthly' | 'yearly';
    recurrence_weekly_days?: number[];
    recurrence_monthly_dates?: number[];
    recurrence_yearly_date?: { month: number; day: number };
    recurrence_end_date?: string;
    allow_user_instances?: boolean;
    instance_template_name?: string;
  }): Promise<ApiResponse> {
    const response = await this.api.post(`/api/groups/${taskData.group_id}/tasks/create/`, taskData);
    return response.data;
  }

  async createTaskWithFiles(groupId: string | null, formData: FormData): Promise<ApiResponse> {
    const url = groupId ? `/api/groups/${groupId}/tasks/create/` : '/api/tasks/create/';
    const response = await this.api.post(url, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  async createTaskProposal(formData: FormData): Promise<ApiResponse> {
    const response = await this.api.post('/api/proposals/', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  async getTaskProposals(filters?: any): Promise<ApiResponse> {
    const response = await this.api.get('/api/proposals/', { params: filters });
    return response.data;
  }

  async getTaskProposalDetails(proposalId: string): Promise<ApiResponse> {
    const response = await this.api.get(`/api/proposals/${proposalId}/`);
    return response.data;
  }

  async approveProposal(proposalId: string, comments: string): Promise<ApiResponse> {
    const response = await this.api.post(`/api/proposals/${proposalId}/approve/`, { comments });
    return response.data;
  }

  async rejectProposal(proposalId: string, reason: string): Promise<ApiResponse> {
    const response = await this.api.post(`/api/proposals/${proposalId}/reject/`, { reason });
    return response.data;
  }

  async withdrawProposalRequest(proposalId: string): Promise<ApiResponse> {
    const response = await this.api.post(`/api/proposals/${proposalId}/withdraw/`);
    return response.data;
  }

  async forwardProposal(proposalId: string, comments: string): Promise<ApiResponse> {
    const response = await this.api.post(`/api/proposals/${proposalId}/forward/`, { comments });
    return response.data;
  }

  async updateGlobalTask(taskId: string, taskData: {
    name?: string;
    description?: string;
    status?: string;
    priority?: string;
    category_id?: string;
    assignee_ids?: string[];
    due_date?: string;
    estimated_hours?: number;
    approval_type?: 'none' | 'single' | 'multiple';
    approver_ids?: string[];
    requires_approval?: boolean;
    requires_documents?: boolean;
    enable_group_chat?: boolean;
    distribution_method?: string;
    direct_assignment_type?: 'instances' | 'single';
    point_allocation?: Record<string, number>;
    work_descriptions?: Record<string, string>;
    is_recurring?: boolean;
    recurrence_type?: 'none' | 'daily' | 'weekly' | 'monthly' | 'yearly';
    recurrence_weekly_days?: number[];
    recurrence_monthly_dates?: number[];
    recurrence_yearly_date?: { month: number; day: number };
    recurrence_end_date?: string;
    allow_user_instances?: boolean;
    instance_template_name?: string;
  }): Promise<ApiResponse> {
    const response = await this.api.put(`/api/tasks/${taskId}/update/`, taskData);
    return response.data;
  }

  async updateTask(taskId: string, groupId: string, taskData: {
    name?: string;
    description?: string;
    status?: string;
    priority?: string;
    category_id?: string;
    assignee_ids?: string[];
    due_date?: string;
    estimated_hours?: number;
    approval_type?: 'none' | 'single' | 'multiple';
    approver_ids?: string[];
    requires_approval?: boolean;
    requires_documents?: boolean;
    enable_group_chat?: boolean;
    distribution_method?: string;
    direct_assignment_type?: 'instances' | 'single';
    point_allocation?: Record<string, number>;
    work_descriptions?: Record<string, string>;
    is_recurring?: boolean;
    recurrence_type?: 'none' | 'daily' | 'weekly' | 'monthly' | 'yearly';
    recurrence_weekly_days?: number[];
    recurrence_monthly_dates?: number[];
    recurrence_yearly_date?: { month: number; day: number };
    recurrence_end_date?: string;
    allow_user_instances?: boolean;
    instance_template_name?: string;
  }): Promise<ApiResponse> {
    const response = await this.api.put(`/api/groups/${groupId}/tasks/${taskId}/update/`, taskData);
    return response.data;
  }

  async updateTaskWithFiles(taskId: string, groupId: string, formData: FormData): Promise<ApiResponse> {
    const response = await this.api.post(`/api/groups/${groupId}/tasks/${taskId}/update/`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  async updateGlobalTaskWithFiles(taskId: string, formData: FormData): Promise<ApiResponse> {
    const response = await this.api.post(`/api/tasks/${taskId}/update/`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  async deleteTask(taskId: string, groupId: string): Promise<ApiResponse> {
    const response = await this.api.delete(`/api/groups/${groupId}/tasks/${taskId}/delete/`);
    return response.data;
  }

  async deleteGlobalTask(taskId: string): Promise<ApiResponse> {
    const response = await this.api.delete(`/api/tasks/${taskId}/delete/`);
    return response.data;
  }

  async getTaskDocuments(groupId: string, taskId: string): Promise<ApiResponse> {
    const response = await this.api.get(`/api/groups/${groupId}/tasks/${taskId}/documents/`);
    return response.data;
  }

  async deleteTaskDocument(groupId: string, taskId: string, documentId: string): Promise<ApiResponse> {
    const response = await this.api.delete(`/api/groups/${groupId}/tasks/${taskId}/documents/${documentId}/delete/`);
    return response.data;
  }

  // User Management APIs
  async getGroupMembers(groupId: string, params?: {
    search?: string;
    role?: string;
    status?: string
  }): Promise<ApiResponse> {
    const response = await this.api.get(`/api/groups/${groupId}/members/`, { params });
    return response.data;
  }

  async searchUsers(query: string): Promise<ApiResponse> {
    const response = await this.api.get(`/api/users/search/`, { params: { q: query } });
    return response.data;
  }

  async addGroupMember(groupId: string, data: {
    email: string;
    role_id: string;
  }): Promise<ApiResponse> {
    const response = await this.api.post(`/api/groups/${groupId}/members/add/`, data);
    return response.data;
  }

  async updateGroupMember(memberId: string, groupId: string, data: {
    role: string;
    status?: string;
    notes?: string;
  }): Promise<ApiResponse> {
    const response = await this.api.put(`/api/groups/${groupId}/members/${memberId}/update/`, data);
    return response.data;
  }

  async removeGroupMember(memberId: string, groupId: string): Promise<ApiResponse> {
    const response = await this.api.delete(`/api/groups/${groupId}/members/${memberId}/remove/`);
    return response.data;
  }

  async addBonusPoints(groupId: string, data: {
    user_id: string;
    points: number;
    reason: string;
    awarded_by?: string;
  }): Promise<ApiResponse> {
    const response = await this.api.post(`/api/groups/${groupId}/bonus-points/`, data);
    return response.data;
  }

  async addBonusPointsToUser(data: {
    user_id: string;
    points: number;
    reason: string;
    group_id?: string;
  }): Promise<ApiResponse> {
    const response = await this.api.post('/api/users/bonus-points/', data);
    return response.data;
  }

  async bulkUpdateMembers(groupId: string, data: {
    member_ids: string[];
    action: 'remove' | 'activate' | 'deactivate';
  }): Promise<ApiResponse> {
    const response = await this.api.post(`/api/groups/${groupId}/members/bulk-update/`, data);
    return response.data;
  }

  // Permission Management API
  async getGroupPermissions(groupId: string): Promise<ApiResponse> {
    const response = await this.api.get(`/api/groups/${groupId}/permissions/`);
    return response.data;
  }

  async updateGroupPermissions(groupId: string, data: {
    admin: string[];
    member: string[];
  }): Promise<ApiResponse> {
    const response = await this.api.put(`/api/groups/${groupId}/permissions/update/`, data);
    return response.data;
  }



  // Dashboard data
  async getDashboardStats(): Promise<ApiResponse> {
    const response = await this.api.get('/api/dashboard/stats/');
    return response.data;
  }

  // MFA endpoints
  async setupMFA(): Promise<ApiResponse> {
    const response = await this.api.get('/api/auth/mfa/setup/');
    return response.data;
  }

  async verifyMFASetup(code: string): Promise<ApiResponse> {
    const response = await this.api.post('/api/auth/mfa/setup/', { code });
    return response.data;
  }

  async verifyMFA(token: string): Promise<AuthResponse> {
    const response = await this.api.post('/api/auth/mfa/verify/', { token });
    return response.data;
  }

  async disableMFA(): Promise<ApiResponse> {
    const response = await this.api.post('/api/auth/mfa/disable/');
    return response.data;
  }

  async generateBackupCodes(): Promise<ApiResponse> {
    const response = await this.api.post('/api/auth/mfa/backup-codes/');
    return response.data;
  }

  async changePassword(passwordData: { current_password: string; new_password: string; confirm_password: string }): Promise<ApiResponse> {
    const response = await this.api.post('/api/auth/change-password/', passwordData);
    return response.data;
  }

  async initiateSSO(provider: string = 'google-oauth2'): Promise<{ status: string; redirect_url?: string; message?: string }> {
    const response = await this.api.post('/api/auth/sso/initiate/', { provider });
    return response.data;
  }

  async getSecuritySettings(): Promise<ApiResponse> {
    const [mfaRes, historyRes] = await Promise.all([
      this.api.get('/api/auth/mfa/status/'),
      this.api.get('/api/auth/login-history/'),
    ]);

    const mfaData = mfaRes.data || {};
    const historyData = historyRes.data || {};

    const loginHistory = Array.isArray(historyData?.data?.login_history)
      ? historyData.data.login_history
      : [];

    return {
      status: 'success',
      message: 'Security settings loaded',
      data: {
        mfa_enabled: mfaData.mfa_enabled ?? false,
        has_backup_codes: mfaData.has_backup_codes ?? false,
        login_history: loginHistory,
      },
    } as ApiResponse;
  }

  async updateSecuritySettings(settings: any): Promise<ApiResponse> {
    if (settings && settings.mfa_enabled === false) {
      // Disable MFA
      const response = await this.api.post('/api/auth/mfa/disable/', settings);
      return response.data;
    } else if (settings && (settings.mfa_enabled === true || settings.mfaEnabled === true)) {
      // Enable MFA - initiate setup to get QR code
      // Note: Full enable requires verification step after this
      const response = await this.api.get('/api/auth/mfa/setup/');
      return response.data;
    }
    return this.getSecuritySettings();
  }

  async getSystemSettings(): Promise<ApiResponse> {
    try {
      console.log('Fetching system settings...');
      const response = await this.api.get('/api/system/settings/');
      console.log('System settings response:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('Error fetching system settings:', error);
      throw error;
    }
  }

  async updateSystemSettings(settings: any): Promise<ApiResponse> {
    try {
      console.log('Updating system settings:', settings);
      const response = await this.api.post('/api/system/settings/update/', settings);
      console.log('System settings update response:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('Error updating system settings:', error);
      throw error;
    }
  }

  // Member Task Management API methods
  async getCampusHierarchy(): Promise<ApiResponse<any>> {
    const response = await this.api.get('/api/campus-hierarchy/');
    return response.data;
  }

  async getHierarchyAssignableUsers(): Promise<ApiResponse<any>> {
    const response = await this.api.get('/api/tasks/assignable-users/');
    return response.data;
  }

  // Permission Management API methods
  async getPermissionGroups(params?: {
    search?: string;
    is_active?: string;
  }): Promise<ApiResponse> {
    const response = await this.api.get('/api/permission-groups/', { params });
    return response.data;
  }

  async getPermissionGroup(groupId: string): Promise<ApiResponse> {
    const response = await this.api.get(`/api/permission-groups/${groupId}/`);
    return response.data;
  }

  async createPermissionGroup(data: {
    name: string;
    description?: string;
    is_active?: boolean;
    permissions?: string[];
  }): Promise<ApiResponse> {
    const response = await this.api.post('/api/permission-groups/create/', data);
    return response.data;
  }

  async updatePermissionGroup(groupId: string, data: {
    name?: string;
    description?: string;
    is_active?: boolean;
    permissions?: string[];
  }): Promise<ApiResponse> {
    const response = await this.api.put(`/api/permission-groups/${groupId}/update/`, data);
    return response.data;
  }

  async deletePermissionGroup(groupId: string): Promise<ApiResponse> {
    const response = await this.api.delete(`/api/permission-groups/${groupId}/delete/`);
    return response.data;
  }

  // Faculty Request Management
  async getFacultyRequests(): Promise<ApiResponse> {
    const response = await this.api.get('/api/auth/faculty/requests/');
    return response.data;
  }

  async manageFacultyRequest(requestId: string, action: 'approve' | 'reject', reason?: string): Promise<ApiResponse> {
    const response = await this.api.post('/api/auth/faculty/requests/', {
      request_id: requestId,
      action,
      reason
    });
    return response.data;
  }


  async getAllPermissions(): Promise<ApiResponse> {
    const response = await this.api.get('/api/permissions/');
    return response.data;
  }

  async getUserPermissionGroups(userId: string): Promise<ApiResponse> {
    const response = await this.api.get(`/api/users/${userId}/permission-groups/`);
    return response.data;
  }

  async updateUserPermissionGroups(userId: string, permissionGroupIds: string[]): Promise<ApiResponse> {
    const response = await this.api.put(`/api/users/${userId}/permission-groups/update/`, {
      permission_group_ids: permissionGroupIds
    });
    return response.data;
  }

  async getUserTasks(params?: {
    search?: string;
    status?: string;
    priority?: string;
    category?: string;
    group?: string;
    due_date?: string;
    work_mode?: 'work' | 'personal';
    user_id?: string;
  }): Promise<ApiResponse> {
    try {
      console.log('Fetching user tasks with params:', params);
      const result = await this.makeRequest('get', '/api/user/tasks/', undefined, { params }) as ApiResponse;
      console.log('User tasks response:', result);
      return result;
    } catch (error: any) {
      console.error('Error fetching user tasks:', error);
      throw error;
    }
  }

  async getAssignedTasks(params?: {
    search?: string;
    status?: string;
    priority?: string;
    category?: string;
    group?: string;
    due_date?: string;
    user_id?: string;
  }): Promise<ApiResponse> {
    try {
      console.log('Fetching assigned tasks with params:', params);
      const result = await this.makeRequest('get', '/api/user/tasks/assigned/', undefined, { params }) as ApiResponse;
      return result;
    } catch (error: any) {
      console.error('Error fetching assigned tasks:', error);
      throw error;
    }
  }

  async getSelfAssignedTasks(params?: {
    search?: string;
    status?: string;
    priority?: string;
    category?: string;
    group?: string;
    due_date?: string;
  }): Promise<ApiResponse> {
    try {
      console.log('Fetching self-assigned tasks with params:', params);
      const result = await this.makeRequest('get', '/api/user/tasks/self-assigned/', undefined, { params }) as ApiResponse;
      return result;
    } catch (error: any) {
      console.error('Error fetching self-assigned tasks:', error);
      throw error;
    }
  }

  async getCreatedTasks(params?: {
    search?: string;
    status?: string;
    priority?: string;
    category?: string;
    group?: string;
    due_date?: string;
    is_template?: string;
  }): Promise<ApiResponse> {
    try {
      console.log('Fetching created tasks with params:', params);
      const result = await this.makeRequest('get', '/api/user/tasks/created/', undefined, { params }) as ApiResponse;
      return result;
    } catch (error: any) {
      console.error('Error fetching created tasks:', error);
      throw error;
    }
  }

  async getUserApprovalTasks(params?: {
    search?: string;
    status?: string;
    priority?: string;
    category?: string;
    group?: string;
    due_date?: string;
    user_id?: string;
  }): Promise<ApiResponse> {
    try {
      console.log('Fetching user approval tasks with params:', params);
      const result = await this.makeRequest('get', '/api/user/approval-tasks/', undefined, { params }) as ApiResponse;
      console.log('User approval tasks response:', result);
      return result;
    } catch (error: any) {
      console.error('Error fetching user approval tasks:', error);
      throw error;
    }
  }

  async updateTaskStatus(taskId: string, groupId: string, data: {
    status: string;
  }): Promise<ApiResponse> {
    try {
      console.log('Updating task status:', { taskId, groupId, data });
      const response = await this.api.patch(`/api/groups/${groupId}/tasks/${taskId}/status/`, data);
      console.log('Task status update response:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('Error updating task status:', error);
      throw error;
    }
  }

  async updateUserAssignmentStatus(taskId: string, groupId: string | null, data: {
    status: string;
  }): Promise<ApiResponse> {
    try {
      console.log('Updating user assignment status:', { taskId, groupId, data });
      // For global tasks (groupId is null), use the global task endpoint
      // For group tasks, use the group-specific endpoint
      const url = groupId
        ? `/api/groups/${groupId}/tasks/${taskId}/assignment-status/`
        : `/api/tasks/${taskId}/assignment-status/`;
      const response = await this.api.patch(url, data);
      console.log('User assignment status update response:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('Error updating user assignment status:', error);
      throw error;
    }
  }

  async uploadTaskDocuments(taskId: string, groupId: string, files: File[]): Promise<ApiResponse> {
    try {
      console.log('Uploading task documents:', { taskId, groupId, files: files.length });

      const formData = new FormData();
      files.forEach((file, index) => {
        formData.append(`document_${index}`, file);
      });

      const response = await this.api.post(`/api/groups/${groupId}/tasks/${taskId}/documents/`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      console.log('Task documents upload response:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('Error uploading task documents:', error);
      throw error;
    }
  }


  // Task Details API methods
  async getUserPerformance(groupId: string, userId: string, period: string = 'all'): Promise<ApiResponse> {
    try {
      console.log('Fetching user performance:', { groupId, userId, period });
      const result = await this.makeRequest('get', `/api/groups/${groupId}/users/${userId}/performance/?period=${period}`) as ApiResponse;
      return result;
    } catch (error: any) {
      console.error('Error fetching user performance:', error);
      return {
        status: 'error',
        message: error.response?.data?.message || 'Failed to fetch user performance',
        data: null
      };
    }
  }

  async getUserPerformanceAllGroups(userId: string, period: string = 'all'): Promise<ApiResponse> {
    try {
      console.log('Fetching user performance (all groups):', { userId, period });
      const result = await this.makeRequest('get', `/api/users/${userId}/performance/?period=${period}`) as ApiResponse;
      return result;
    } catch (error: any) {
      console.error('Error fetching user performance (all groups):', error);
      return {
        status: 'error',
        message: error.response?.data?.message || 'Failed to fetch user performance',
        data: null
      };
    }
  }

  async getFacultyAppraisalReport(userId: string, academicYear?: string): Promise<ApiResponse> {
    const params: any = {};
    if (academicYear) params.academic_year = academicYear;
    const response = await this.api.get(`/api/users/${userId}/appraisal-report/`, { params });
    return response.data;
  }

  async downloadAppraisalPdf(userId: string, academicYear?: string): Promise<Blob> {
    const params: any = {};
    if (academicYear) params.academic_year = academicYear;

    const response = await this.api.get(`/api/users/${userId}/appraisal-report/pdf/`, {
      params,
      responseType: 'blob'
    });
    return response.data;
  }

  async getTaskDetails(taskId: string, params?: {
    filter_comments_by_user?: boolean;
    work_mode?: 'work' | 'personal';
  }): Promise<ApiResponse> {
    try {
      console.log('Fetching task details:', { taskId, params });
      const result = await this.makeRequest('get', `/api/tasks/${taskId}/details/`, undefined, { params }) as ApiResponse;
      console.log('Task details response:', result);
      return result;
    } catch (error: any) {
      console.error('Error fetching task details:', error);
      throw error;
    }
  }

  // Template Statistics API methods
  async getTemplateStatistics(templateId: string): Promise<ApiResponse> {
    try {
      const result = await this.makeRequest('get', `/api/tasks/${templateId}/template/statistics/`) as ApiResponse;
      return result;
    } catch (error: any) {
      console.error('Error fetching template statistics:', error);
      throw error;
    }
  }

  async getTemplateAssignees(templateId: string): Promise<ApiResponse> {
    try {
      const result = await this.makeRequest('get', `/api/tasks/${templateId}/template/assignees/`) as ApiResponse;
      return result;
    } catch (error: any) {
      console.error('Error fetching template assignees:', error);
      throw error;
    }
  }

  async getCounsellingRecords(params?: any): Promise<any> {
    try {
      return await this.makeRequest('get', '/api/counselling/', undefined, { params });
    } catch (error: any) {
      console.error('Error fetching counselling records:', error);
      throw error;
    }
  }

  async downloadCounsellingReport(recordId: string): Promise<Blob> {
    try {
      const response = await this.api.get(`/api/counselling/${recordId}/download_report/`, {
        responseType: 'blob'
      });
      return response.data;
    } catch (error: any) {
      console.error('Error downloading counselling report:', error);
      throw error;
    }
  }

  async getTemplateInstances(templateId: string, params?: { group_id?: string; status?: string }): Promise<ApiResponse> {
    try {
      const result = await this.makeRequest('get', `/api/tasks/${templateId}/template/instances/`, { params }) as ApiResponse;
      return result;
    } catch (error: any) {
      console.error('Error fetching template instances:', error);
      throw error;
    }
  }

  async addTaskComment(taskId: string, groupId: string, data: {
    content: string;
    action_type?: string;
    attachments?: File[];
    parent_comment?: string;
  }): Promise<ApiResponse> {
    try {
      console.log('Adding task comment:', { taskId, groupId, data });

      const formData = new FormData();
      formData.append('content', data.content);
      formData.append('action_type', data.action_type || 'general');

      if (data.parent_comment) {
        formData.append('parent_comment', data.parent_comment);
      }

      // Add file attachments
      if (data.attachments && data.attachments.length > 0) {
        data.attachments.forEach((file, index) => {
          formData.append(`attachment_${index}`, file);
        });
      }

      const response = await this.api.post(`/api/groups/${groupId}/tasks/${taskId}/comments/`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      console.log('Add comment response:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('Error adding task comment:', error);
      throw error;
    }
  }

  async addTaskCommentReaction(taskId: string, groupId: string, commentId: string, emoji: string): Promise<ApiResponse> {
    try {
      console.log('Adding task comment reaction:', { taskId, groupId, commentId, emoji });

      const response = await this.api.post(`/api/groups/${groupId}/tasks/${taskId}/comments/${commentId}/reactions/`, {
        emoji: emoji
      });
      console.log('Add reaction response:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('Error adding task comment reaction:', error);
      throw error;
    }
  }

  // Task Proposals
  async createProposal(data: any): Promise<ApiResponse> {
    try {
      console.log('Creating task proposal:', data);
      const response = await this.api.post('/api/proposals/', data);
      console.log('Create proposal response:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('Error creating task proposal:', error);
      throw error;
    }
  }

  async getMyProposals(params?: {
    page?: number;
    per_page?: number;
    search?: string;
    status?: string;
  }): Promise<PaginatedApiResponse<TaskProposal[]>> {
    const response = await this.api.get('/api/proposals/my_proposals/', { params });
    return response.data;
  }

  async getPendingProposalApprovals(params?: {
    page?: number;
    per_page?: number;
    search?: string;
  }): Promise<PaginatedApiResponse<TaskProposal[]>> {
    const response = await this.api.get('/api/proposals/pending_approvals/', { params });
    return response.data;
  }

  async deleteTaskComment(taskId: string, groupId: string, commentId: string): Promise<ApiResponse> {
    try {
      console.log('Deleting task comment:', { taskId, groupId, commentId });

      const response = await this.api.delete(`/api/groups/${groupId}/tasks/${taskId}/comments/${commentId}/`);
      console.log('Delete comment response:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('Error deleting task comment:', error);
      throw error;
    }
  }

  async submitTaskCompletion(taskId: string, groupId: string | null, data: {
    content: string;
    action_type: string;
    attachments: File[];
    requires_documents: boolean;
  }): Promise<ApiResponse> {
    try {
      console.log('Submitting task completion:', { taskId, groupId, data });

      const formData = new FormData();
      // Only append group_id if it's not null (for group tasks)
      // For global tasks, groupId will be null and we don't append it
      if (groupId !== null && groupId !== undefined) {
        formData.append('group_id', groupId);
      }
      formData.append('content', data.content);
      formData.append('action_type', data.action_type);
      formData.append('requires_documents', data.requires_documents.toString());

      // Add file attachments
      if (data.attachments && data.attachments.length > 0) {
        data.attachments.forEach((file, index) => {
          formData.append(`attachment_${index}`, file);
        });
      }

      const response = await this.api.post(`/api/tasks/${taskId}/submit_completion/`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      console.log('Task completion submission response:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('Error submitting task completion:', error);
      throw error;
    }
  }

  // Generic HTTP methods for custom endpoints
  async get(url: string, config?: any): Promise<ApiResponse> {
    return this.makeRequest('get', url, undefined, config);
  }

  async post(url: string, data?: any, config?: any): Promise<ApiResponse> {
    return this.makeRequest('post', url, data, config);
  }

  async put(url: string, data?: any, config?: any): Promise<ApiResponse> {
    return this.makeRequest('put', url, data, config);
  }

  async patch(url: string, data?: any, config?: any): Promise<ApiResponse> {
    return this.makeRequest('patch', url, data, config);
  }

  // Task Submissions and Proof of Work
  async getTaskSubmissions(taskId: string, groupId: string | null): Promise<ApiResponse> {
    const params: any = {};
    if (groupId) {
      params.group_id = groupId;
    }
    return this.makeRequest('get', `/api/tasks/${taskId}/submissions/`, undefined, {
      params
    }) as Promise<ApiResponse>;
  }

  async getPendingApprovals(groupId?: string): Promise<ApiResponse> {
    return this.makeRequest('get', '/api/submissions/pending_approvals/', undefined, {
      params: groupId ? { group_id: groupId } : {}
    }) as Promise<ApiResponse>;
  }

  async approveProofOfWork(submissionId: string, reviewNotes: string = '', qualityGrade: string = 'A'): Promise<ApiResponse> {
    return this.makeRequest('post', `/api/submissions/${submissionId}/approve/`, {
      review_notes: reviewNotes,
      quality_grade: qualityGrade
    }) as Promise<ApiResponse>;
  }

  async rejectProofOfWork(submissionId: string, rejectionReason: string, rejectionDetails: string = '', reviewNotes: string = ''): Promise<ApiResponse> {
    return this.makeRequest('post', `/api/submissions/${submissionId}/reject/`, {
      rejection_reason: rejectionReason,
      rejection_details: rejectionDetails,
      review_notes: reviewNotes
    }) as Promise<ApiResponse>;
  }

  async resubmitProofOfWork(submissionId: string, resubmissionData: any): Promise<ApiResponse> {
    const formData = new FormData();
    formData.append('content', resubmissionData.content || '');
    formData.append('action_type', resubmissionData.action_type || 'resubmission');

    // Add attachments if provided
    if (resubmissionData.attachments && resubmissionData.attachments.length > 0) {
      resubmissionData.attachments.forEach((file: File) => {
        formData.append('attachments', file);
      });
    }

    return this.makeRequest('post', `/api/submissions/${submissionId}/resubmit/`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    }) as Promise<ApiResponse>;
  }

  async getUserSubmissions(taskId: string, groupId: string | null, userId?: string): Promise<ApiResponse> {
    const params: any = {};
    if (groupId) {
      params.group_id = groupId;
    }
    if (userId) {
      params.user_id = userId;
    }
    return this.makeRequest('get', `/api/tasks/${taskId}/user_submissions/`, undefined, {
      params
    }) as Promise<ApiResponse>;
  }

  // Permission endpoints
  async getUserPermissions(): Promise<ApiResponse> {
    return this.makeRequest('get', '/api/user/permissions/') as Promise<ApiResponse>;
  }

  // Platform Reports endpoints
  async getPlatformReports(params?: { period?: string; filter?: string }): Promise<ApiResponse> {
    return this.makeRequest('get', '/api/platform-reports/', undefined, {
      params
    }) as Promise<ApiResponse>;
  }

  async delete(url: string, config?: any): Promise<ApiResponse> {
    return this.makeRequest('delete', url, undefined, config);
  }

  // Utility method to clear pending requests (useful for logout or error scenarios)
  clearPendingRequests(): void {
    console.log(`Clearing ${this.pendingRequests.size} pending requests`);
    this.pendingRequests.clear();
  }

  // Method to get current pending requests count (for debugging)
  getPendingRequestsCount(): number {
    return this.pendingRequests.size;
  }

  getBaseURL(): string {
    return this.baseURL;
  }

  async getNotifications(params?: {
    type?: string;
    is_read?: boolean;
    page?: number;
    page_size?: number;
  }): Promise<ApiResponse> {
    const queryParams = new URLSearchParams();
    if (params?.type) queryParams.append('type', params.type);
    if (params?.is_read !== undefined) queryParams.append('is_read', params.is_read.toString());
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.page_size) queryParams.append('page_size', params.page_size.toString());

    const url = `/api/notifications/${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
    const response = await this.api.get(url);
    return response.data;
  }

  async getNotification(notificationId: string): Promise<ApiResponse> {
    const response = await this.api.get(`/api/notifications/${notificationId}/`);
    return response.data;
  }

  async markNotificationAsRead(notificationId: string): Promise<ApiResponse> {
    const response = await this.api.patch(`/api/notifications/${notificationId}/`, {
      is_read: true
    });
    return response.data;
  }

  async markNotificationAsUnread(notificationId: string): Promise<ApiResponse> {
    const response = await this.api.patch(`/api/notifications/${notificationId}/`, {
      is_read: false
    });
    return response.data;
  }

  async deleteNotification(notificationId: string): Promise<ApiResponse> {
    const response = await this.api.delete(`/api/notifications/${notificationId}/`);
    return response.data;
  }

  async bulkNotificationAction(action: 'mark_as_read' | 'mark_as_unread' | 'delete', notificationIds: string[]): Promise<ApiResponse> {
    const response = await this.api.post('/api/notifications/bulk-action/', {
      action,
      notification_ids: notificationIds
    });
    return response.data;
  }

  async getNotificationStats(): Promise<ApiResponse> {
    const response = await this.api.get('/api/notifications/stats/');
    return response.data;
  }

  async getNotificationSummary(): Promise<ApiResponse> {
    const response = await this.api.get('/api/notifications/summary/');
    return response.data;
  }

  async getNotificationPreferences(): Promise<ApiResponse> {
    const response = await this.api.get('/api/notifications/preferences/');
    return response.data;
  }

  async updateNotificationPreferences(preferences: {
    email_enabled?: boolean;
    push_enabled?: boolean;
    sms_enabled?: boolean;
    in_app_enabled?: boolean;
    quiet_hours_enabled?: boolean;
    quiet_hours_start?: string;
    quiet_hours_end?: string;
    digest_enabled?: boolean;
    digest_time?: string;
    type_preferences?: Record<string, any>;
  }): Promise<ApiResponse> {
    const response = await this.api.patch('/api/notifications/preferences/', preferences);
    return response.data;
  }
  async getAcademicYears(): Promise<any> {
    const response = await this.api.get('/api/academic/academic-years/');
    return response.data;
  }

  async initiateTaskInstance(formData: FormData): Promise<ApiResponse> {
    const response = await this.api.post('/api/tasks/initiate-instance/', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }
}

export const apiService = new ApiService();
export default apiService;
